import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type HTMLAttributes } from 'react';

import {
  alertsApi,
  type AlertChannel,
  type AlertEvent,
} from '../../api/notifications.api.js';
import { settingsHistoryKey } from '../../api/settings-history.api.js';

import { routePendingChanges } from './alert-route-changes.js';
import { SettingsHistoryButton, useSettingsLastChanged } from './SettingsHistory.js';
import { noRestoreNote } from './settings-sections.js';
import { SettingsSaveBar } from './SettingsSaveBar.js';
import { SettingsSectionHeader } from './SettingsSectionHeader.js';

/**
 * Who finds out, and where.
 *
 * Two halves, deliberately. A CHANNEL is a Slack destination a team owns —
 * "#payments", "#compliance". A ROUTE points one kind of event at one channel.
 * Kept apart because teams own channels and events do not: eight payment events
 * at "#payments" is eight routes referencing one webhook, so moving that team
 * to a different channel is one edit rather than eight, and the eventual missed
 * edit is an event quietly going nowhere.
 *
 * An event with no channel is not lost. Everything is recorded in the console
 * regardless; a route only decides whether Slack is told as well.
 */

const GROUP_LABELS: Readonly<Record<AlertEvent['group'], string>> = {
  DEPOSITS: 'Money coming in',
  WITHDRAWALS: 'Money going out',
  ONBOARDING: 'Onboarding and identity',
  ACCOUNTS: 'Accounts',
  COMPLIANCE: 'Compliance',
  TREASURY: 'Treasury',
};

const GROUP_ORDER: readonly AlertEvent['group'][] = [
  'DEPOSITS',
  'WITHDRAWALS',
  'ONBOARDING',
  'ACCOUNTS',
  'COMPLIANCE',
  'TREASURY',
];

function ChannelEditor({ channels }: { channels: AlertChannel[] }): JSX.Element {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['alert-routing'] });
    void queryClient.invalidateQueries({ queryKey: settingsHistoryKey('ALERT_ROUTING') });
  };

  const save = useMutation({
    mutationFn: (input: { id?: string; name: string; webhookUrl?: string; isActive?: boolean }) =>
      alertsApi.saveChannel(input),
    onSuccess: () => {
      setName('');
      setWebhookUrl('');
      setError(null);
      invalidate();
    },
    onError: (err: unknown) =>
      setError(err instanceof Error ? err.message : 'The channel could not be saved.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => alertsApi.deleteChannel(id),
    onSuccess: invalidate,
  });

  const urlLooksWrong = webhookUrl !== '' && !webhookUrl.startsWith('https://hooks.slack.com/');

  return (
    <Paper sx={{ p: 2, mb: 4 }}>
      <Typography variant="subtitle1" gutterBottom>
        Slack channels
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        A destination a team watches. Create one per team, then point events at it below. The
        webhook is stored encrypted and never shown again — anyone holding it can post into that
        channel as us.
      </Typography>

      {channels.length > 0 && (
        <Stack spacing={1} sx={{ mb: 2 }}>
          {channels.map((channel) => (
            <Stack key={channel.id} direction="row" alignItems="center" gap={1}>
              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                {channel.name}
              </Typography>
              <Chip
                size="small"
                variant="outlined"
                label={channel.isActive ? 'On' : 'Off'}
                color={channel.isActive ? 'success' : 'default'}
                onClick={() =>
                  save.mutate({ id: channel.id, name: channel.name, isActive: !channel.isActive })
                }
              />
              <Button
                size="small"
                color="error"
                disabled={remove.isPending}
                onClick={() => remove.mutate(channel.id)}
              >
                Remove
              </Button>
            </Stack>
          ))}
        </Stack>
      )}

      <Divider sx={{ my: 2 }} />

      <Stack direction={{ xs: 'column', md: 'row' }} gap={2} alignItems="flex-start">
        <TextField
          size="small"
          label="Channel name"
          placeholder="Compliance — #aml"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ minWidth: 240 }}
        />
        <TextField
          size="small"
          fullWidth
          label="Slack incoming webhook"
          placeholder="https://hooks.slack.com/services/..."
          value={webhookUrl}
          onChange={(e) => setWebhookUrl(e.target.value)}
          error={urlLooksWrong}
          helperText={
            urlLooksWrong ? 'A Slack incoming webhook starts with https://hooks.slack.com/' : ' '
          }
        />
        <Button
          variant="contained"
          disabled={save.isPending || name.trim() === '' || webhookUrl === '' || urlLooksWrong}
          onClick={() => save.mutate({ name: name.trim(), webhookUrl })}
          sx={{ mt: 0.5 }}
        >
          Add channel
        </Button>
      </Stack>

      {error !== null && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Paper>
  );
}

export function AlertRoutingPage(): JSX.Element {
  const queryClient = useQueryClient();
  const lastChanged = useSettingsLastChanged('ALERT_ROUTING');

  const events = useQuery({ queryKey: ['alert-events'], queryFn: () => alertsApi.events() });
  const routing = useQuery({ queryKey: ['alert-routing'], queryFn: () => alertsApi.routing() });
  // Route choices not saved yet, by event (DEV-137): the save bar saves them together.
  const [edits, setEdits] = useState<Readonly<Record<string, string | null>>>({});
  const [savedAt, setSavedAt] = useState(0);

  const saveRoutes = useMutation({
    mutationFn: (routes: ReadonlyArray<{ eventKey: string; channelId: string | null }>) => alertsApi.setRoutes(routes),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['alert-routing'] });
      void queryClient.invalidateQueries({ queryKey: settingsHistoryKey('ALERT_ROUTING') });
      setEdits({});
      setSavedAt(Date.now());
    },
  });

  if (events.isLoading || routing.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (events.isError || routing.isError) {
    return <Alert severity="error">Could not load the alert settings.</Alert>;
  }

  const channels = routing.data?.channels ?? [];
  const routes = new Map((routing.data?.routes ?? []).map((r) => [r.eventKey, r]));
  const all = events.data ?? [];
  const changes = routePendingChanges(all, channels, routing.data?.routes ?? [], edits);

  return (
    <Box>
      <SettingsSectionHeader
        title="Alert routing"
        lastChanged={lastChanged}
        actions={<SettingsHistoryButton subject="ALERT_ROUTING" title="Alert routing" restoreNote={noRestoreNote({ group: 'notifications', id: 'alerts' })} />}
      >
        Everything below is always recorded in this console and appears under the bell. A channel
        decides whether Slack is told as well — so an event with no channel is quieter, never lost.
        Messages carry no amounts and no customer names: a chat room is a nudge to come and look,
        not the record. Every channel and route change is recorded in the History.
      </SettingsSectionHeader>

      <ChannelEditor channels={channels} />

      {GROUP_ORDER.map((group) => {
        const inGroup = all.filter((e) => e.group === group);
        if (inGroup.length === 0) return null;
        return (
          <Paper key={group} sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ px: 2, pt: 2 }}>
              {GROUP_LABELS[group]}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Event</TableCell>
                  <TableCell sx={{ width: 280 }}>Slack channel</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inGroup.map((event) => {
                  const route = routes.get(event.key);
                  const chosen = event.key in edits ? edits[event.key] : (route?.channelId ?? null);
                  return (
                    <TableRow key={event.key} hover>
                      <TableCell>
                        <Stack direction="row" alignItems="center" gap={1}>
                          <Typography variant="body2">{event.label}</Typography>
                          {!event.live && (
                            // Honesty: routing this would mean watching a
                            // channel for a message that cannot arrive yet.
                            <Tooltip title="Nothing in the platform raises this yet. It is listed so the shape is visible.">
                              <Chip size="small" label="Not raised yet" variant="outlined" />
                            </Tooltip>
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {event.detail}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={chosen ?? ''}
                          disabled={saveRoutes.isPending}
                          slotProps={{
                            select: { SelectDisplayProps: { 'aria-label': `Slack channel for ${event.label}` } as HTMLAttributes<HTMLDivElement> },
                          }}
                          onChange={(e) =>
                            setEdits((prev) => ({ ...prev, [event.key]: e.target.value === '' ? null : e.target.value }))
                          }
                        >
                          <MenuItem value="">Console only</MenuItem>
                          {channels.map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                              {c.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        );
      })}

      <SettingsSaveBar
        sticky
        changes={changes}
        saving={saveRoutes.isPending}
        onSave={() => saveRoutes.mutate(changes.map((c) => ({ eventKey: c.key, channelId: edits[c.key] ?? null })))}
        onDiscard={() => setEdits({})}
        {...(saveRoutes.isError
          ? { error: saveRoutes.error instanceof Error ? saveRoutes.error.message : 'The routes could not be saved.' }
          : {})}
        savedAt={savedAt}
      />
    </Box>
  );
}
